import Alpine from 'alpinejs';
import DailyIframe, { DailyCall } from '@daily-co/daily-js';
import { apiClient } from './api-client';
import type { CalendarEvent, CalendarStatus, Persona, Conversation } from '../shared/types';

// Initialize Alpine.js
window.Alpine = Alpine;

// Store Daily call object outside Alpine's reactive system
let dailyCallObject: DailyCall | null = null;

// Alpine component interface
interface AvatarAppData {
  // State
  personas: Persona[];
  selectedPersona: Persona | null;
  conversations: Conversation[];
  conversationsLoading: boolean;
  showChoiceScreen: boolean;
  activeTab: 'conversations' | 'personas' | 'meeting';
  meetingUrl: string;
  botName: string;
  joinWhen: 'now' | 'in10' | 'scheduled';
  scheduledTime: string;
  meetingLoading: boolean;
  inCall: boolean;
  hasJoinedCall: boolean;
  kaiConversationId: number | null;
  loading: boolean;
  initialLoading: boolean;
  personasLoading: boolean;
  error: string | null;
  successMessage: string | null;
  isRecording: boolean;
  recordingLoading: boolean;
  recordingInfo: { action?: string; type?: string; recordingId?: string; startedBy?: string } | null;
  replicaThumbnails: Record<string, string>;

  // Auth State
  currentUser: { id: string; email: string; name: string } | null;
  showLoginModal: boolean;
  loginMode: 'demo' | 'credentials';
  loginEmail: string;
  loginPassword: string;
  loginError: string | null;
  loginErrorLink: string | null;
  loggingIn: boolean;
  // MFA state
  mfaRequired: boolean;
  mfaSession: string;
  mfaMethod: string;
  mfaEmail: string;
  mfaCode: string;
  mfaError: string | null;
  mfaLoading: boolean;

  // Calendar state
  calendarStatus: CalendarStatus | null;
  calendarEvents: CalendarEvent[];
  calendarEventsLoading: boolean;
  calendarBotLoadingId: string | null;

  // Methods
  init(): Promise<void>;
  checkAuth(): Promise<void>;
  handleLogin(): Promise<void>;
  handleCredentialsLogin(): Promise<void>;
  handleMfaSubmit(): Promise<void>;
  onLoginSuccess(user: { id: string; email: string; name: string }): Promise<void>;
  handleLogout(): Promise<void>;
  loadPersonas(): Promise<void>;
  loadConversations(): Promise<void>;
  personaInitials(name: string): string;
  getStatusColor(status?: string): string;
  getStatusLabel(status?: string): string;
  selectPersona(persona: Persona): void;
  loadReplicaThumbnail(persona: Persona): Promise<void>;
  startCall(): Promise<void>;
  joinCall(url: string): Promise<void>;
  handleCallEnd(): void;
  joinMeeting(): Promise<void>;
  loadCalendarStatus(): Promise<void>;
  loadCalendarEvents(): Promise<void>;
  connectCalendar(provider: string): void;
  disconnectCalendar(): Promise<void>;
  toggleBotForEvent(event: CalendarEvent): Promise<void>;
  formatEventTime(isoString: string): string;
  leaveCall(): void;
  abandonConversation(): void;
  scheduledTimeMin(): string;
  detectedPlatform(): 'zoom' | 'meet' | 'teams' | null;
  destroy(): void;
}

// Avatar App Component
Alpine.data('avatarApp', (): AvatarAppData => ({
  // State
  personas: [],
  selectedPersona: null,
  conversations: [],
  conversationsLoading: false,
  showChoiceScreen: false,
  activeTab: 'personas',
  meetingUrl: '',
  botName: 'KAI Assistant',
  joinWhen: 'now' as 'now' | 'in10' | 'scheduled',
  scheduledTime: '',
  meetingLoading: false,
  inCall: false,
  hasJoinedCall: false,
  kaiConversationId: null,
  loading: false,
  initialLoading: true,
  personasLoading: false,
  error: null,
  successMessage: null,
  isRecording: false,
  recordingLoading: false,

  // Auth State
  currentUser: null,
  showLoginModal: false,
  loginMode: 'demo' as 'demo' | 'credentials',
  loginEmail: '',
  loginPassword: '',
  loginError: null,
  loginErrorLink: null,
  loggingIn: false,
  mfaRequired: false,
  mfaSession: '',
  mfaMethod: '',
  mfaEmail: '',
  mfaCode: '',
  mfaError: null,
  mfaLoading: false,
  recordingInfo: null,
  replicaThumbnails: {},

  // Calendar state
  calendarStatus: null,
  calendarEvents: [],
  calendarEventsLoading: false,
  calendarBotLoadingId: null,

  focusField(id: string) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        (document.getElementById(id) as HTMLInputElement | null)?.focus();
      });
    });
  },

  // Methods
  async init() {
    try {
      // Check authentication status
      await this.checkAuth();

      // If not authenticated, show login modal
      if (!this.currentUser) {
        this.showLoginModal = true;
        this.focusField('email');
      } else {
        // Load conversations and personas if authenticated
        this.showChoiceScreen = true;
        await Promise.all([this.loadConversations(), this.loadPersonas()]);
      }
      const params = new URLSearchParams(window.location.search);
      if (params.has('calendarConnected') || params.has('calendarError')) {
        this.activeTab = 'meeting';
        this.showChoiceScreen = false;
        if (params.has('calendarConnected')) {
          this.successMessage = 'Calendar connected! Toggle the bot on for any meetings below.';
        }
        if (params.has('calendarError')) {
          const reason = params.get('calendarError');
          this.error = reason === 'access_denied'
            ? 'Calendar connection was cancelled.'
            : reason === 'not_configured'
              ? 'That calendar provider is not configured on this server yet.'
              : 'Failed to connect calendar. Please try again.';
        }
        window.history.replaceState({}, '', window.location.pathname);
      }

      if (this.activeTab === 'meeting') {
        void this.loadCalendarStatus();
      }
    } finally {
      this.initialLoading = false;
    }
  },

  async checkAuth() {
    try {
      const response = await fetch('/api/auth/me', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        this.currentUser = data.user;
      }
    } catch (err) {
      console.error('Auth check failed:', err);
    }
  },

  async handleLogin() {
    this.loggingIn = true;
    this.loginError = null;
    this.loginErrorLink = null;

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: this.loginEmail }),
      });

      if (!response.ok) {
        const error = await response.json();
        if (response.status === 403) {
          this.loginMode = 'credentials';
          this.loggingIn = false;
          this.focusField('password');
          return;
        }
        throw new Error(error.error || 'Login failed');
      }

      const data = await response.json();
      await this.onLoginSuccess(data.user);
    } catch (err) {
      this.loginError = err instanceof Error ? err.message : 'Login failed';
    } finally {
      this.loggingIn = false;
    }
  },

  async handleCredentialsLogin() {
    this.loggingIn = true;
    this.loginError = null;
    this.loginErrorLink = null;

    try {
      const response = await fetch('/api/auth/login-with-credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: this.loginEmail, password: this.loginPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      if (data.mfaRequired) {
        this.mfaRequired = true;
        this.mfaSession = data.session;
        this.mfaMethod = data.mfaMethod;
        this.mfaEmail = data.email;
        this.loginPassword = '';
        this.focusField('mfa-code');
        return;
      }

      await this.onLoginSuccess(data.user);
    } catch (err) {
      this.loginError = err instanceof Error ? err.message : 'Login failed';
    } finally {
      this.loggingIn = false;
    }
  },

  async handleMfaSubmit() {
    this.mfaLoading = true;
    this.mfaError = null;

    try {
      const response = await fetch('/api/auth/login-mfa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: this.mfaEmail,
          session: this.mfaSession,
          type: this.mfaMethod,
          code: this.mfaCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      await this.onLoginSuccess(data.user);
    } catch (err) {
      this.mfaError = err instanceof Error ? err.message : 'Verification failed';
    } finally {
      this.mfaLoading = false;
    }
  },

  async onLoginSuccess(user: { id: string; email: string; name: string }) {
    this.currentUser = user;
    this.showLoginModal = false;
    this.loginEmail = '';
    this.loginPassword = '';
    this.mfaRequired = false;
    this.mfaCode = '';
    this.mfaSession = '';

    const redirect = new URLSearchParams(window.location.search).get('redirect');
    if (redirect) {
      window.location.href = redirect;
      return;
    }

    this.showChoiceScreen = true;
    await Promise.all([this.loadConversations(), this.loadPersonas()]);
  },

  async handleLogout() {
    try {
      if (this.inCall) {
        const callObj = dailyCallObject;
        dailyCallObject = null;
        this.abandonConversation();
        if (callObj) void callObj.destroy();
      }

      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });

      this.currentUser = null;
      this.personas = [];
      this.conversations = [];
      this.activeTab = 'personas';
      this.showChoiceScreen = false;
      this.showLoginModal = true;
      this.focusField('email');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  },

  async loadPersonas() {
    this.error = null;
    this.personasLoading = true;

    try {
      console.log('Loading personas from API...');
      const response = await apiClient.getPersonas();
      this.personas = response.data;
      console.log(`Loaded ${this.personas.length} personas`);

      // Auto-select first persona if available
      if (this.personas.length > 0 && !this.selectedPersona) {
        this.selectedPersona = this.personas[0];
      }

      // Load replica thumbnails for each persona
      const personaThumbnailPromises = this.personas.map(persona => persona.default_replica_id &&this.loadReplicaThumbnail(persona));

      await Promise.all(personaThumbnailPromises);
    } catch (err) {
      console.error('Error loading personas:', err);
      this.error = err instanceof Error ? err.message : 'Failed to load personas';
    } finally {
      this.personasLoading = false;
    }
  },

  async loadConversations() {
    this.conversationsLoading = true;

    try {
      console.log('Loading conversations from API...');
      const response = await fetch('/api/conversations', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        // Sort conversations by date (newest first)
        this.conversations = data.conversations.sort((a: Conversation, b: Conversation) => {
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        });
        console.log(`Loaded ${this.conversations.length} conversations`);
      } else if (response.status === 401) {
        // Not authenticated, no conversations
        this.conversations = [];
      } else {
        throw new Error('Failed to load conversations');
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
      this.conversations = [];
      // Don't show error to user for conversations - just show empty state
    } finally {
      this.conversationsLoading = false;
    }
  },

  selectPersona(persona: Persona) {
    this.selectedPersona = persona;
    this.error = null;
  },

  personaInitials(name: string): string {
    if (!name) return '';
    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    } else if (words.length === 2){
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    } else {
      return (words[1].charAt(0) + words[2].charAt(0)).toUpperCase();
    }
  },

  // Pill colours as unified-ui's StatusPill variants (green, blue, amber, red, gray).
  getStatusColor(status?: string): string {
    switch (status?.toLowerCase()) {
      case 'published':
      case 'finalized':
        return 'bg-success-soft text-success';
      case 'transcribing':
      case 'analyzing':
      case 'finalizing':
      case 'transcribed':
      case 'analyzed':
        return 'bg-brand-soft text-brand';
      case 'review_required':
        return 'bg-warning-soft text-warning';
      case 'error':
      case 'rejected':
        return 'bg-danger-soft text-danger';
      default:
        return 'bg-surface-2 text-ink-2';
    }
  },

  getStatusLabel(status?: string): string {
    if (!status) return 'Unknown';

    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  },

  async loadReplicaThumbnail(persona: Persona) {
    if (!persona.default_replica_id) return;

    try {
      const replica = await apiClient.getReplica(persona.default_replica_id);
      this.replicaThumbnails[persona.persona_id] = replica.thumbnail_video_url;
    } catch (err) {
      console.error(`Error loading replica thumbnail for ${persona.persona_name}:`, err);
      // Don't show error to user, just skip thumbnail
    }
  },

  async startCall() {
    if (!this.selectedPersona) {
      this.error = 'Please select a persona';
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      console.log('Creating new conversation via backend API...');

      const result = await apiClient.createConversation({
        personaId: this.selectedPersona.persona_id,
        conversationName: `${this.selectedPersona.persona_name} - ${new Date().toISOString()}`,
        context: this.selectedPersona.system_prompt,
        greeting: `Hi! I'm ${this.selectedPersona.persona_name}. How can I help you today?`,
        replicaId: this.selectedPersona.default_replica_id || undefined,
      });

      this.kaiConversationId = result.kaiConversationId;
      this.hasJoinedCall = false;
      history.pushState(null, '', `?conversation=${result.kaiConversationId}`);

      console.log('Conversation created successfully');
      this.successMessage = 'Conversation created successfully!';
      setTimeout(() => this.successMessage = null, 3000);

      // Join the call using Daily.js
      await this.joinCall(result.conversationUrl);

    } catch (err) {
      console.error('Error starting call:', err);
      this.error = err instanceof Error ? err.message : 'Failed to start conversation. Please try again.';
      this.loading = false;
    }
  },

  async joinCall(url: string) {
    try {
      // Convert to plain string to avoid Alpine proxy issues
      const plainUrl = String(url);
      console.log('[JOIN] Starting join process with URL:', plainUrl);

      // Get DOM element outside Alpine's reactive context
      const container = document.getElementById('video-container');
      console.log('[JOIN] Video container element:', container);
      if (!container) {
        throw new Error('Video container not found');
      }

      // Store reference to component context
      const component = this;

      console.log('[JOIN] Creating Daily iframe...');
      // Create Daily call object with url parameter
      const callObj = DailyIframe.createFrame(container, {
        iframeStyle: {
          width: '100%',
          height: '100%',
          border: '0',
          borderRadius: '16px'
        },
        showLeaveButton: true,
        showFullscreenButton: true,
      });
      // The call is a navy island in either theme: unified-ui's dark tokens.
      await callObj.setTheme({
        colors: {
          accent: '#0058FF',
          accentText: '#FFFFFF',
          background: '#020919',
          backgroundAccent: '#111432',
          baseText: '#F5F6FA',
          border: '#262A55',
          mainAreaBg: '#020919',
          mainAreaBgAccent: '#161A3D',
          mainAreaText: '#F5F6FA',
          supportiveText: '#8D90AE',
        },
      });

      console.log('[JOIN] Daily iframe created:', callObj);

      // Set up event listeners with component reference
      callObj.on('joined-meeting', function() {
        console.log('Joined meeting successfully');
        component.hasJoinedCall = true;
        callObj.startRecording();
      });

      callObj.on('left-meeting', function() {
        console.log('Left meeting');
        component.handleCallEnd();
      });

      callObj.on('error', function(error: any) {
        console.error('Daily error:', error);
        component.error = `Call error: ${error.errorMsg || 'Unknown error'}`;
        component.loading = false;
      });

      callObj.on('recording-started', function(event: any) {
        console.log('Recording started:', event);
        component.isRecording = true;
        component.recordingInfo = {
          action: event.action,
          type: event.type,
          recordingId: event.recordingId,
          startedBy: event.startedBy,
        };
        component.recordingLoading = false;
        component.successMessage = 'Recording started successfully!';
        setTimeout(() => component.successMessage = null, 3000);
      });

      callObj.on('recording-stopped', function(event: any) {
        console.log('Recording stopped:', event);
        component.isRecording = false;
        component.recordingInfo = {
          action: event.action,
          type: event.type,
          recordingId: event.recordingId,
        };
        component.recordingLoading = false;
        component.successMessage = 'Recording stopped. Your recording will be available shortly.';
        setTimeout(() => component.successMessage = null, 5000);
      });

      callObj.on('recording-error', function(event: any) {
        console.error('Recording error:', event);
        component.error = `Recording error: ${event.errorMsg || 'Unknown error'}`;
        component.recordingLoading = false;
      });

      // Store the call object outside Alpine's reactive system
      dailyCallObject = callObj;

      console.log('Setting inCall to true...');
      component.inCall = true;
      component.loading = false;
      console.log('[JOIN] Calling join()');
      await callObj.join({
        url: plainUrl,
        showLeaveButton: true,
      });

    } catch (err) {
      console.error('[JOIN] Error joining call:', err);
      this.error = err instanceof Error ? err.message : 'Failed to join call';
      this.loading = false;
    }
  },

  abandonConversation() {
    if (!this.hasJoinedCall && this.kaiConversationId !== null) {
      const id = this.kaiConversationId;
      void apiClient.deleteConversation(id).catch(err =>
        console.error('Failed to delete abandoned conversation:', err)
      );
    }
    this.kaiConversationId = null;
    this.hasJoinedCall = false;
    history.replaceState(null, '', window.location.pathname);
  },

  leaveCall() {
    const callObj = dailyCallObject;
    dailyCallObject = null;
    this.abandonConversation();
    this.inCall = false;
    this.isRecording = false;
    this.recordingInfo = null;
    this.loading = false;
    this.recordingLoading = false;
    this.showChoiceScreen = true;
    if (callObj) void callObj.destroy();
  },

  handleCallEnd() {
    const callObj = dailyCallObject;
    dailyCallObject = null;
    // User completed the call normally — preserve the conversation for webhook processing
    this.kaiConversationId = null;
    this.hasJoinedCall = false;
    history.replaceState(null, '', window.location.pathname);
    this.inCall = false;
    this.isRecording = false;
    this.recordingInfo = null;
    this.loading = false;
    this.recordingLoading = false;
    this.showChoiceScreen = true;
    if (callObj) void callObj.destroy();
  },

  scheduledTimeMin() {
    // Returns the current datetime in the format required by datetime-local input
    const now = new Date();
    now.setSeconds(0, 0);
    return now.toISOString().slice(0, 16);
  },

  detectedPlatform() {
    try {
      const host = new URL(this.meetingUrl).hostname.toLowerCase();
      if (host.includes('zoom.us') || host.includes('zoom.com')) return 'zoom';
      if (host.includes('meet.google.com')) return 'meet';
      if (host.includes('teams.microsoft.com') || host.includes('teams.live.com')) return 'teams';
    } catch {
      // not a valid URL yet
    }
    return null;
  },

  async joinMeeting() {
    if (!this.meetingUrl) {
      this.error = 'Please enter a meeting URL';
      return;
    }
    if (!this.detectedPlatform()) {
      this.error = 'Please enter a valid Zoom, Google Meet, or Microsoft Teams URL';
      return;
    }

    this.meetingLoading = true;
    this.error = null;

    try {
      let joinAt: string | undefined;

      if (this.joinWhen === 'in10') {
        const t = new Date(Date.now() + 10 * 60 * 1000);
        joinAt = t.toISOString();
      } else if (this.joinWhen === 'scheduled') {
        if (!this.scheduledTime) {
          this.error = 'Please select a time';
          this.meetingLoading = false;
          return;
        }
        joinAt = new Date(this.scheduledTime).toISOString();
      }

      await apiClient.joinMeeting({
        meetingUrl: this.meetingUrl,
        botName: this.botName || 'KAI Assistant',
        joinAt,
      });

      this.successMessage = joinAt
        ? `Meeting bot scheduled for ${new Date(joinAt).toLocaleString()}`
        : 'Meeting bot is joining now!';
      this.meetingUrl = '';
      this.joinWhen = 'now';
      this.scheduledTime = '';
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to join meeting';
    } finally {
      this.meetingLoading = false;
    }
  },

  async loadCalendarStatus() {
    try {
      this.calendarStatus = await apiClient.getCalendarStatus();
      if (this.calendarStatus.connected) {
        void this.loadCalendarEvents();
      }
    } catch {
      // non-fatal
    }
  },

  async loadCalendarEvents() {
    this.calendarEventsLoading = true;
    try {
      const { events } = await apiClient.getCalendarEvents();
      this.calendarEvents = events;
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load calendar events';
    } finally {
      this.calendarEventsLoading = false;
    }
  },

  connectCalendar(provider: string) {
    window.location.href = `/api/calendar/oauth/start?provider=${provider}`;
  },

  async disconnectCalendar() {
    try {
      await apiClient.disconnectCalendar();
      this.calendarStatus = { connected: false };
      this.calendarEvents = [];
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to disconnect calendar';
    }
  },

  async toggleBotForEvent(event: CalendarEvent) {
    this.calendarBotLoadingId = event.id;
    try {
      if (event.botScheduled) {
        await apiClient.removeBotFromEvent(event.id);
      } else {
        await apiClient.scheduleBotForEvent(
          event.id,
          this.botName || 'KAI Assistant',
          event.title,
          event.description,
          event.meetingUrl,
          event.attendees,
        );
      }
      this.calendarEvents = this.calendarEvents.map(e =>
        e.id === event.id ? { ...e, botScheduled: !e.botScheduled } : e
      );
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to update bot for event';
    } finally {
      this.calendarBotLoadingId = null;
    }
  },

  formatEventTime(isoString: string) {
    return new Date(isoString).toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  },

  // Cleanup on component destroy
  destroy() {
    if (dailyCallObject) {
      void dailyCallObject.destroy();
      dailyCallObject = null;
    }
  }
}));

// Start Alpine
Alpine.start();
